import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ForeplayAd } from '@/types'
import { ReferenceAdCard } from './ReferenceAdCard'

export interface CollapsibleAdSectionProps {
  title: string
  count: number
  expanded: boolean
  onToggle: () => void
  variant?: 'product' | 'competitor'
  /** Either provide children (for custom rendering) or ads array */
  children?: React.ReactNode
  /** If provided, renders ReferenceAdCard grid automatically */
  ads?: ForeplayAd[]
  selectedAdIds?: string[]
  onSelectAd?: (adId: string) => void
  onPreviewAd?: (ad: ForeplayAd) => void
  /** Map of ad ID to score (for when scores come from separate scoring call) */
  scoredAdsMap?: Map<string, number>
  /** Disable all selections (e.g., during scoring) */
  disabled?: boolean
}

export function CollapsibleAdSection({
  title,
  count,
  expanded,
  onToggle,
  variant = 'product',
  children,
  ads,
  selectedAdIds = [],
  onSelectAd,
  onPreviewAd,
  scoredAdsMap,
  disabled = false,
}: CollapsibleAdSectionProps) {
  const accentColor = variant === 'product' ? 'text-blue-600' : 'text-orange-600'

  // Render either children or auto-generated grid
  const renderContent = () => {
    if (children) {
      return children
    }

    if (ads && onSelectAd) {
      // Debug: log scoredAdsMap usage
      if (scoredAdsMap && scoredAdsMap.size > 0) {
        console.log(`[CollapsibleAdSection ${title}] scoredAdsMap size:`, scoredAdsMap.size)
        console.log(`[CollapsibleAdSection ${title}] First ad ID:`, ads[0]?.id, 'has score:', scoredAdsMap.get(ads[0]?.id))
      }
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {ads.map((ad) => {
            const score = scoredAdsMap?.get(ad.id)
            return (
              <ReferenceAdCard
                key={ad.id}
                ad={ad}
                score={score}
                isSelected={selectedAdIds.includes(ad.id)}
                onSelect={onSelectAd}
                onPreview={onPreviewAd}
                variant={variant}
                disabled={disabled}
              />
            )
          })}
        </div>
      )
    }

    return null
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{title}</span>
          <span className={`text-sm ${accentColor}`}>({count} ads)</span>
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-gray-500 transition-transform',
            expanded && 'rotate-180'
          )}
        />
      </button>

      {/* Content */}
      {expanded && <div className="p-4">{renderContent()}</div>}
    </div>
  )
}
